import {useCallback, useEffect, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Hotel} from '@findhotel/sapi'
import {getParamsDetection} from 'hooks/useRecentlyUsedFilters/useRecentlyUsedFilters'
import {getHotel, getHotelOfferEntityOffers} from 'modules/sapiSearch/selectors'
import {setIsAnchorHotelHidden} from 'modules/search/slice'

import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'

// Added as part of sapi4eva-anchor-offer-level-filters experiment
export const useHideAnchorHotel = (hotelId: Hotel['objectID']) => {
  const {getQueryParams} = useRouterProvider()

  const dispatch = useDispatch()
  const toggle = useToggle()

  const filters = useMemo(
    () => getParamsDetection(getQueryParams),
    [getQueryParams]
  )
  const hotel = useSelector(state => getHotel(state, hotelId))
  const hotelOfferEntity = useSelector(state =>
    getHotelOfferEntityOffers(state, hotelId)
  )

  const getShouldHideBasedHotelLevelFilters = useCallback(() => {
    if (!hotel) return false
    let unmatchedFiltersCount = 0

    if (filters?.facilities) {
      for (const facility of filters.facilities) {
        const facilityValue = (hotel?.facilities || [])?.includes(
          parseInt(facility)
        )
        if (!facilityValue) unmatchedFiltersCount++
      }
    }

    if (filters?.starRatings && hotel.starRating !== undefined) {
      const starRatingValue = filters.starRatings.includes(
        hotel.starRating.toString()
      )
      if (!starRatingValue) unmatchedFiltersCount++
    }

    if (filters?.reviewScore) {
      const reviewScoreValue =
        parseInt(filters.reviewScore) <= (hotel.guestRating?.overall ?? 0)
      if (!reviewScoreValue) unmatchedFiltersCount++
    }

    if (filters?.themes) {
      for (const theme of filters.themes) {
        const themeValue = (hotel?.themeIds || [])?.includes(parseInt(theme))
        if (!themeValue) unmatchedFiltersCount++
      }
    }

    return unmatchedFiltersCount > 0
  }, [filters, hotel])

  const getShouldHideBasedOfferLevelFilters = useCallback(() => {
    if (
      !hotel ||
      !filters ||
      hotelOfferEntity === undefined ||
      hotelOfferEntity.length > 0
    )
      return false

    if (
      filters.payLater ||
      filters.priceMax > 0 ||
      filters.priceMin > 0 ||
      filters.freeCancellation ||
      filters.amenities?.length > 0 ||
      filters.exclusive ||
      filters.dealFreeze
    )
      return true

    return false
  }, [filters, hotelOfferEntity, hotel])

  useEffect(() => {
    const shouldHideBasedHotelLevelFilters =
      getShouldHideBasedHotelLevelFilters()
    const shouldHideBasedOfferLevelFilters =
      getShouldHideBasedOfferLevelFilters()

    const shouldHideHotel =
      shouldHideBasedHotelLevelFilters || shouldHideBasedOfferLevelFilters

    if (shouldHideHotel) {
      dispatch(
        setIsAnchorHotelHidden(
          toggle('sapi4eva-anchor-offer-level-filters', false, true)
        )
      )
    } else {
      dispatch(setIsAnchorHotelHidden(false))
    }
  }, [
    filters,
    hotel,
    toggle,
    dispatch,
    getShouldHideBasedHotelLevelFilters,
    getShouldHideBasedOfferLevelFilters
  ])

  return {
    hotelName: hotel?.hotelName
  }
}
